import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Dialog } from "../../../components/Dialogs/Dialog";
import { useDispatchWrapper } from "../../../hooks";
import {
  handleCompanySwitchAsync,
  IAuthUserCompanyOutlet,
  useAuth,
} from "../../../contexts/AuthProvider/AuthProvider";
import { saveBillingCompanyInStorage } from "../../../providers/BillingCompanyProvider/BillingCompanyProvider.storage";
import { showMessage } from "../../../redux/messages/messagesActions";

interface ISelectCompanyInfoProps {
  open: boolean;
  onClose: () => void;
}

export const BillingInfoDialog: React.FC<ISelectCompanyInfoProps> = (props) => {
  const { open, onClose } = props;
  const {
    user: { affilated_company_outlet_uuids },
  } = useAuth();

  const dispatch = useDispatchWrapper();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [companyToSwitch, setCompanyToSwitch] =
    useState<IAuthUserCompanyOutlet | null>(null);

  // const handleSelectBillingCompany = (company: IAuthUserCompanyOutlet) => {
  //   if (company) {
  //     saveBillingCompanyInStorage(company);
  //     window.location.reload();
  //   }
  //   onClose();
  // };

  const handleSelectBillingCompany = (company: IAuthUserCompanyOutlet) => {
    if (!company || isLoading) return;

    // Show confirmation dialog first
    setCompanyToSwitch(company);
    setShowConfirmation(true);
  };

  const handleConfirmSwitch = async () => {
    if (!companyToSwitch || isLoading) return;

    try {
      setIsLoading(true);
      setSelectedCompany(companyToSwitch.company_outlet_uuid);
      setShowConfirmation(false);

      // Call the API to switch company
      await new Promise<void>((resolve, reject) => {
        handleCompanySwitchAsync(
          companyToSwitch.company_outlet_uuid,
          (isSuccess) => {
            if (isSuccess) {
              resolve();
            } else {
              reject(new Error("Failed to switch company"));
            }
          },
          (error) => {
            reject(new Error(error));
          }
        );
      });

      // If API call succeeds, save to storage and redirect
      saveBillingCompanyInStorage(companyToSwitch);

      dispatch(
        showMessage({
          type: "success",
          message: `Successfully switched to ${companyToSwitch.company_outlet_name}`,
          displayAs: "snackbar",
        })
      );

      // Close dialog
      onClose();

      // Clear all navigation history and redirect to dashboard
      // This prevents users from using browser back button to see previous company data
      window.history.replaceState(null, "", "/dashboard");
      window.location.href = "/dashboard";
    } catch (error: any) {
      console.error("Error switching company:", error);

      dispatch(
        showMessage({
          type: "error",
          message:
            error.message || "Failed to switch company. Please try again.",
          displayAs: "snackbar",
        })
      );
    } finally {
      setIsLoading(false);
      setSelectedCompany(null);
      setCompanyToSwitch(null);
    }
  };

  const handleCancelSwitch = () => {
    setShowConfirmation(false);
    setCompanyToSwitch(null);
  };

  return (
    <>
      <Dialog
        open={open}
        title="Select Company Info"
        onClose={onClose}
        size="sm"
      >
        <List>
          {affilated_company_outlet_uuids.map((company) => {
            const isSelected = selectedCompany === company.company_outlet_uuid;
            const isDisabled = isLoading;

            return (
              <ListItem key={company.company_outlet_uuid} disablePadding>
                <ListItemButton
                  onClick={() => handleSelectBillingCompany(company)}
                  disabled={isDisabled}
                  sx={{
                    opacity: isDisabled && !isSelected ? 0.6 : 1,
                    position: "relative",
                  }}
                >
                  <ListItemText
                    primary={company.company_outlet_name}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  />
                  {isSelected && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={16} />
                      <span style={{ fontSize: "0.875rem", color: "#666" }}>
                        Switching...
                      </span>
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={handleCancelSwitch}
        title="Confirm Company Switch"
        size="sm"
        actions={[
          {
            type: "button",
            variant: "contained",
            label: "No",
            disabled: isLoading,
            onClick: handleCancelSwitch,
          },
          {
            type: "button",
            variant: "contained",
            label: isLoading ? (
              <>
                <CircularProgress size={16} style={{ marginRight: 8 }} />
                Switching...
              </>
            ) : (
              "Yes"
            ),
            disabled: isLoading,
            onClick: handleConfirmSwitch,
          },
        ]}
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to switch to{" "}
          <strong>{companyToSwitch?.company_outlet_name}</strong>?
        </Typography>
      </Dialog>
    </>
  );
};
